import { ReactNode, useEffect, useState } from 'react'
import styled from 'styled-components'
import { TableSearch } from './Filters/TableSearch'
import { dataTableSortingStoreRepo } from './DataTable/DataTableSorting/DataTableSortingStore'
import { filterStoreRepo } from './Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { SortSelect } from './SortSelect'
import { MobileDealsFilterset } from 'pages/app/deals/MobileDealsFilterset'
import { PiFunnel } from 'react-icons/pi'
import TableButton from './Buttons/TableButtons/TableButton'
import { MobileFiltersModal } from './MobileFiltersModal'
import MobileContactsFilterset from './Tables/ContactsTable/MobileContactsFilterset'
import { MobileLocationsFilterset } from './Tables/SelectLocationsForDealTable/MobileLocationsFilterset'
import { MobileSamplesFilterset } from './Samples/MobileSamplesFilterset'
import { MobileRemindersFilterset } from './Reminders/MobileRemindersFilterset'
import { MobileNotesFilterset } from './Notes/MobileNotesFilterset'

type SortOption = {
  label: string
  value: string
}

const SearchInputContainer = styled.div`
  input {
    height: 38px;
  }
`

type Props = {
  filterStoreIdentifier: FilterIdentifier
  tableKey: string
  sortingOptions?: { label: ReactNode; value: string }[]
  defaultSorting?: { desc: boolean; id: string }[]
}

export function MobileListViewHeader({
  filterStoreIdentifier,
  tableKey,
  sortingOptions,
  defaultSorting,
}: Props) {
  const [focused, setFocused] = useState<boolean>(false)
  const filterStore = filterStoreRepo.getStore(filterStoreIdentifier)
  const sortingStore = dataTableSortingStoreRepo.getStore(tableKey)()
  const { sorting, setSorting } = sortingStore
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false)
  const { applyFilters } = filterStore()

  useEffect(() => {
    if (defaultSorting) {
      setSorting(defaultSorting)
    }
  }, [])

  function renderFilterset() {
    switch (filterStoreIdentifier) {
      case 'DEALS_TABLE':
        return (
          <MobileFiltersModal
            title="Deals Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileDealsFilterset filterIdentifier={filterStoreIdentifier} />
          </MobileFiltersModal>
        )
      case 'SELECT_LOCATIONS':
        return (
          <MobileFiltersModal
            title="Locations Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileLocationsFilterset
              filterIdentifier={filterStoreIdentifier}
            />
          </MobileFiltersModal>
        )
      case 'CONTACTS_TABLE':
        return (
          <MobileFiltersModal
            title="Contacts Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileContactsFilterset filterIdentifier={filterStoreIdentifier} />
          </MobileFiltersModal>
        )
      case 'MOBILE_DOORS_LIST':
        return (
          <MobileFiltersModal
            title="Doors Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileLocationsFilterset
              filterIdentifier={filterStoreIdentifier}
            />
          </MobileFiltersModal>
        )
      case 'MOBILE_SAMPLES_TABLE':
        return (
          <MobileFiltersModal
            title="Samples Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileSamplesFilterset filterIdentifier={filterStoreIdentifier} />
          </MobileFiltersModal>
        )
      case 'MOBILE_REMINDERS_TABLE':
        return (
          <MobileFiltersModal
            title="Reminders Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileRemindersFilterset
              filterIdentifier={filterStoreIdentifier}
            />
          </MobileFiltersModal>
        )
      case 'MOBILE_NOTES_TABLE':
        return (
          <MobileFiltersModal
            title="Notes Filters"
            onClose={() => setShowFilterModal(false)}
            onSubmit={() => {
              applyFilters()
              setShowFilterModal(false)
            }}
          >
            <MobileNotesFilterset filterIdentifier={filterStoreIdentifier} />
          </MobileFiltersModal>
        )
      default:
        return null
    }
  }

  if (showFilterModal) {
    return renderFilterset()
  }

  return (
    <div className="flex w-full gap-2 items-center p-2">
      <SearchInputContainer
        className={`
            flex-1 transition-all duration-300
            ${focused ? 'flex-[2]' : 'flex-1'}
          `}
      >
        <TableSearch
          className="w-full"
          filterStore={filterStore}
          searchPlaceholder="Search by name, company..."
          tableKey={tableKey}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </SearchInputContainer>
      <div
        className={`
            flex items-center gap-2 overflow-hidden transition-all duration-300
            ${focused ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[600px]'}
          `}
      >
        {!!sortingOptions?.length && (
          <SortSelect
            options={sortingOptions}
            value={sortingOptions.find((op) => {
              const isDesc = op.value.startsWith('-')
              const val = isDesc ? op.value.split('-')[1] : op.value
              return isDesc === sorting?.[0]?.desc && val === sorting?.[0]?.id
            })}
            onChange={(option) => {
              const newValue = option as SortOption
              const isDesc = newValue.value.startsWith('-')
              const val = isDesc ? newValue.value.split('-')[1] : newValue.value
              sortingStore.setSorting([{ id: val, desc: isDesc }])
            }}
          />
        )}

        <TableButton
          text="Filter"
          icon={PiFunnel}
          activeValue={'0'}
          onClick={() => setShowFilterModal(true)}
          type="button"
        />
      </div>
    </div>
  )
}
